/**
 * useLeadSources — shared hook that returns the merged (builtin + custom)
 * list of lead sources. Cached at module scope so switching pages doesn't
 * refetch. Exposes `addSource(label)` to POST a new custom source and
 * automatically append it to the cached list.
 */
import { useCallback, useEffect, useState } from "react";
import { SOURCES as BUILTIN_SOURCES } from "./constants";
import api from "./api";

// Module-scope cache — first component that mounts fetches; the rest reuse.
let _cache = null;
let _inflight = null;
const _subs = new Set();

const notify = () => _subs.forEach((fn) => fn(_cache));

async function _fetch() {
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const { data } = await api.get("/lead-sources");
      _cache = data.sources || [];
    } catch {
      // Backend unreachable — fall back to builtin list so the app still works
      _cache = BUILTIN_SOURCES.map((s) => ({ ...s, builtin: true, active: true }));
    }
    notify();
    return _cache;
  })();
  const out = await _inflight;
  _inflight = null;
  return out;
}

export default function useLeadSources() {
  const [sources, setSources] = useState(_cache || []);
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    const listener = (v) => setSources(v || []);
    _subs.add(listener);
    if (!_cache) _fetch().finally(() => setLoading(false));
    else setLoading(false);
    return () => { _subs.delete(listener); };
  }, []);

  const addSource = useCallback(async (label) => {
    const clean = String(label || "").trim();
    if (!clean) throw new Error("Source name required");
    const { data } = await api.post("/lead-sources", { label: clean });
    // Update cache
    if (!_cache) _cache = [];
    if (!_cache.find((s) => s.id === data.id)) {
      _cache = [..._cache, { id: data.id, label: data.label,
                             builtin: !!data.builtin, active: true }];
      notify();
    }
    return data;
  }, []);

  const reload = useCallback(async () => {
    _cache = null;
    await _fetch();
  }, []);

  return { sources, loading, addSource, reload };
}
